@use "styles/abstracts/variables" as v;
@use "styles/abstracts/mixins/common" as m;
@use "styles/abstracts/mixins/typography" as t;

.stuffing-detailed {
  display: flex;
  flex-direction: column;
  gap: 8px;

  &__header,
  &__footer {
    display: flex;
    justify-content: center;
  }
}

.card {
  display: flex;
  gap: 40px;
  align-items: stretch;
  box-sizing: border-box;
  width: 100%;
  min-height: 312px;
  padding: 24px;
  background: v.$palette-white;
  border-radius: 10px;
  box-shadow: v.$shadow2;
  font-family: v.$primary-font;
  color: v.$palette-gray-900;

  @media (max-width: 900px) {
    flex-direction: column;
    gap: 20px;
    min-height: 0;
    padding: 16px;
    border-radius: 12px;
  }
}

.preview {
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 0 0 260px;
  width: 260px;
  min-width: 220px;

  @media (max-width: 900px) {
    width: 100%;
    flex-basis: auto;
    min-width: 0;
  }
}

.previewHeader {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
}

.title,
.unitCount {
  margin: 0;
  @include t.text(16px, 26px, "semi-bold");
  color: v.$palette-gray-900;
}

.unitCount {
  white-space: nowrap;
}

.previewImage {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: 160px;
  width: 100%;
  overflow: hidden;

  img {
    display: block;
    width: 100%;
    max-height: 180px;
    object-fit: contain;
  }
}

.view3d {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  align-self: flex-start;
  padding: 5px 16px;
  border: 0;
  border-radius: 6px;
  background: v.$palette-orange-2;
  color: v.$palette-white;

  @media (pointer: fine) {
    &:hover {
      color: v.$palette-white;
      background: v.$palette-orange-3;
    }
  }
  @include t.text(14px, 22px, "semi-bold");
  cursor: pointer;
  font-family: inherit;

  @media (max-width: 900px) {
    align-self: stretch;
    min-height: 40px;
  }
}

.view3dIcon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  overflow: hidden;

  img {
    display: block;
    width: 14px;
    height: 14px;
  }
}

.divider {
  width: 1px;
  align-self: stretch;
  background: v.$palette-dirty-40;
  flex-shrink: 0;

  @media (max-width: 900px) {
    width: 100%;
    height: 1px;
  }
}

.details {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 16px;
  flex: 1;
  min-width: 0;
  min-height: 264px;

  @media (max-width: 900px) {
    min-height: 0;
  }
}

.detailsTop {
  display: flex;
  gap: 32px;
  align-items: flex-start;
  width: 100%;
  min-width: 0;

  @media (max-width: 900px) {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
}

.chart {
  position: relative;
  flex-shrink: 0;
  width: 120px;
  height: 120px;

  @media (max-width: 900px) {
    align-self: center;
  }
}

.chartLabel {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  text-align: center;
  left: 10px;
  top: 9px;
}

.chartPercent {
  @include t.text(21px, 26px, "bold");
  color: v.$palette-gray-900;
  letter-spacing: 0.09px;
}

.chartFilled {
  font-family: v.$primary-font;
  font-size: 11px;
  line-height: 14px;
  font-weight: 400;
  color: #696974;
  letter-spacing: 0.08px;
}

.products {
  display: flex;
  gap: 30px;
  justify-content: space-between;
  align-items: flex-start;
  flex: 1;
  min-width: 0;
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-x: contain;
  padding-bottom: 8px;

  @include m.media(v.$container-desktop, "min") {
    overflow-x: visible;
    padding-bottom: 4px;
  }
}

.colGroup,
.colProduct,
.colPackage,
.colQty,
.colVolume,
.colWeight {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  max-width: max-content;
}

.colGroup {
  flex: 0 0 auto;
  width: 120px;

  @include m.media(v.$container-desktop, "min") {
    flex: 1.2 1 0;
    width: auto;
  }
}

.colProduct {
  flex: 0 0 auto;
  width: 140px;

  @include m.media(v.$container-desktop, "min") {
    flex: 1.4 1 0;
    width: auto;
  }
}

.colPackage {
  flex: 0 0 auto;
  width: 120px;

  @include m.media(v.$container-desktop, "min") {
    flex: 1.2 1 0;
    width: auto;
  }
}

.colQty {
  flex: 0 0 auto;
  width: 50px;
  min-width: 0;

  @include m.media(v.$container-desktop, "min") {
    flex: 0.5 1 0;
    width: auto;
    min-width: 40px;
  }
}

.colVolume,
.colWeight {
  flex: 0 0 auto;
  width: 80px;
  min-width: 0;

  @include m.media(v.$container-desktop, "min") {
    flex: 0.8 1 0;
    width: auto;
    min-width: 64px;
  }
}

.colHeader {
  @include t.text(10px, 12px, "bold");
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: v.$palette-gray-900;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.colBody {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  min-width: 0;
}

.rowCell,
.productCell,
.packageCell,
.valueCell {
  display: flex;
  align-items: center;
  min-height: 22px;
  box-sizing: border-box;
}

.groupName {
  @include t.text(14px, 22px, "semi-bold");
  color: v.$palette-gray-900;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.productCell {
  gap: 8px;
  width: 100%;
  min-width: 0;
}

.dot {
  display: block;
  width: 8px;
  height: 8px;
  border-radius: 20px;
  flex-shrink: 0;
}

.productName {
  @include t.text(14px, 22px, "semi-bold");
  color: v.$palette-gray-900;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
  min-width: 0;
}

.packageCell {
  gap: 8px;
  width: 100%;
  min-width: 0;
}

.packageIcon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  overflow: hidden;

  svg {
    display: block;
    width: 16px;
    height: 16px;
  }
}

.packageLabel {
  @include t.text(14px, 22px, "normal");
  color: v.$palette-gray-900;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.valueCell {
  @include t.text(14px, 22px, "normal");
  color: v.$palette-gray-900;
  white-space: nowrap;
  width: 100%;
}

.summary {
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  width: 100%;
  padding: 8px 16px;
  border-radius: 16px;
  background: v.$palette-dirty-10;
  @include t.text(14px, 22px, "normal");
  color: v.$palette-gray-900;
}

.summaryLabels,
.summaryValues {
  display: flex;
  gap: 4px;
  align-items: flex-start;
  flex-wrap: wrap;

  > span {
    width: 140px;
    flex-shrink: 0;

    @media (max-width: 560px) {
      width: calc(50% - 4px);
      min-width: 120px;
    }
  }
}

.summaryLabels {
  @include t.text(14px, 22px, "semi-bold");
}

.summaryValues {
  @include t.text(14px, 22px, "normal");
}

.stuffing-item {
  display: none;
}

.stuffing-chart {
  position: relative;
}
